import config from '@yaak/components/services/api/config'
import { SimDeviceInfo } from '@yaak/components/services/api/api'
import { apiFetch, defaultHeaders } from '@yaak/components/services/api/headers'
import { Dispatch, SetStateAction } from 'react'
import { toastType, ToastTypes } from '@yaak/components/src/Toast/Toast'

export type { SimDeviceInfo }

export type SimDeviceInfoPayload = Record<string, unknown>

const processResponse = async ({
  response,
  onAlert,
}: {
  response: Response
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<SimDeviceInfo | undefined> => {
  if (
    response.status === 200 ||
    response.status === 201 ||
    response.status === 202 ||
    response.status === 204
  ) {
    const responseBody = await response.text()
    if (responseBody.length < 1) {
      return undefined
    }

    return JSON.parse(responseBody)
  }

  try {
    const text = await response.text()
    const parsed = JSON.parse(text)

    if (response.status === 403 && parsed.logout === true) {
      localStorage.clear()
    }

    if (response.status === 404) {
      return undefined
    }

    onAlert({
      text: `${parsed.code}: ${parsed.message}`,
      type: ToastTypes.error,
    })
  } catch {
    if (response.status === 404) {
      return undefined
    }

    onAlert({
      text: `${response.status}: ${response.statusText}`,
      type: ToastTypes.error,
    })
  }

  return undefined
}

export const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

export const parseSimDeviceInfoPayload = (
  payload: unknown
): SimDeviceInfoPayload | null => {
  if (payload === null || payload === undefined) {
    return null
  }

  if (isPlainObject(payload)) {
    return payload
  }

  if (typeof payload !== 'string' || payload.length === 0) {
    return null
  }

  try {
    const parsed = JSON.parse(payload)

    if (isPlainObject(parsed)) {
      return parsed
    }

    return null
  } catch {
    return null
  }
}

export const sortObjectEntries = (
  entries: [string, unknown][]
): [string, unknown][] =>
  [...entries].sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))

export const isStatusKey = (key: string): boolean =>
  key === 'status' || key.endsWith('_status')

export const isTimestampKey = (key: string): boolean =>
  key.endsWith('_at') || key.endsWith('At') || key.endsWith('_timestamp')

export const getChecksFromPayload = (
  payload: SimDeviceInfoPayload
): Record<string, unknown>[] | null => {
  const checks = payload.checks

  if (!Array.isArray(checks)) {
    return null
  }

  return checks.filter(isPlainObject)
}

export const getSnapshotSummaryEntries = (
  payload: SimDeviceInfoPayload
): [string, unknown][] =>
  sortObjectEntries(
    Object.entries(payload).filter(([key, value]) => {
      if (key === 'checks' && Array.isArray(value)) {
        return false
      }

      return !Array.isArray(value) || value.length === 0 || !value.every(isPlainObject)
    })
  )

export const formatSimDeviceInfoValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return ''
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : 'false'
  }

  if (typeof value === 'object') {
    return JSON.stringify(value)
  }

  return String(value)
}

export const getOverallStatusFromPayload = (
  payload?: unknown
): string | undefined => {
  const parsed = parseSimDeviceInfoPayload(payload)
  if (!parsed) {
    return undefined
  }

  const overallStatus = parsed.overall_status
  return typeof overallStatus === 'string' && overallStatus.length > 0
    ? overallStatus
    : undefined
}

export const getOverallStatusFromSimRigInfo = (
  info?: SimDeviceInfo | Record<string, unknown> | null
): string | undefined => {
  if (!info) {
    return undefined
  }

  if (isPlainObject(info) && 'payload' in info) {
    return getOverallStatusFromPayload(info.payload)
  }

  return getOverallStatusFromPayload(info)
}

export const getCheckLabel = (check: Record<string, unknown>, index: number): string => {
  for (const [key, value] of sortObjectEntries(Object.entries(check))) {
    if (typeof value !== 'string' || value.length === 0) {
      continue
    }

    if (isStatusKey(key) || key === 'message' || isTimestampKey(key)) {
      continue
    }

    return value
  }

  return `Check ${index + 1}`
}

export const getSimDeviceInfo = ({
  token,
  simDeviceId,
  onAlert,
}: {
  token: string
  simDeviceId: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<SimDeviceInfo | undefined> => {
  return apiFetch(`${config.apiUrl}/v1/sim/devices/${simDeviceId}/info`, {
    credentials: 'same-origin',
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })

      return undefined
    })
}
